<script setup>
/**
 * ToastHost —— 应用根挂载一次，渲染 use-toast 的受控队列。只读队列、不写入；
 * 写入一律走 `useToast()`。每条 toast 可手动关闭（点 × 调 dismiss）。
 */

import { useToast, useToastQueue } from '../composables/use-toast.js'

defineProps({
  banner: { type: Boolean, default: false },
  dismissible: { type: Boolean, default: true },
})

const toasts = useToastQueue()
const { dismiss } = useToast()
</script>

<template>
  <Teleport to="body">
    <div
      class="drama-toast-host"
      :class="{ 'drama-toast-host--banner': banner }"
      role="region"
      aria-live="polite"
    >
      <div
        v-for="t in toasts"
        :key="t.id"
        class="drama-toast"
        :class="`drama-toast--${t.type}`"
        :role="t.type === 'error' ? 'alert' : 'status'"
      >
        <slot
          name="message"
          :toast="t"
        >
          <span class="drama-toast__msg">{{ t.message }}</span>
        </slot>
        <button
          v-if="dismissible"
          class="drama-toast__close"
          type="button"
          aria-label="Dismiss"
          @click="dismiss(t.id)"
        >
          ×
        </button>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.drama-toast-host {
  position: fixed;
  top: var(--drama-space-lg);
  right: var(--drama-space-lg);
  display: flex;
  flex-direction: column;
  gap: var(--drama-space-sm);
  z-index: 1100;
  pointer-events: none;
}

.drama-toast {
  display: flex;
  align-items: center;
  gap: var(--drama-space-md);
  min-width: 240px;
  max-width: 360px;
  padding: var(--drama-space-md) var(--drama-space-lg);
  border-radius: var(--drama-radius-md);
  border-left: 4px solid var(--drama-color-border);
  background-color: var(--drama-color-surface);
  color: var(--drama-color-text);
  box-shadow: var(--drama-shadow-md);
  font-family: var(--drama-font-family-body);
  font-size: var(--drama-font-size-sm);
  pointer-events: auto;
}

.drama-toast--success {
  border-left-color: var(--drama-color-success);
}

.drama-toast--error {
  border-left-color: var(--drama-color-danger);
}

.drama-toast--info {
  border-left-color: var(--drama-color-info);
}

.drama-toast__msg {
  flex: 1;
}

.drama-toast__close {
  background: transparent;
  border: none;
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-lg);
  line-height: 1;
  cursor: pointer;
}
.drama-toast-host--banner {
  top: calc(var(--drama-space-sm) + env(safe-area-inset-top, 0px));
  right: var(--drama-space-sm);
  left: var(--drama-space-sm);
}

.drama-toast-host--banner .drama-toast {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  max-width: none;
  border-left: 0;
  border-radius: var(--drama-radius-md);
  text-align: center;
}
.drama-toast-host--banner .drama-toast--error {
  background-color: color-mix(in srgb, var(--drama-color-danger) 12%, var(--drama-color-surface));
}
.drama-toast-host--banner .drama-toast--success {
  background-color: color-mix(in srgb, var(--drama-color-success) 14%, var(--drama-color-surface));
}
@media (min-width: 681px) {
  .drama-toast-host--banner {
    top: var(--drama-space-xl);
    right: auto;
    left: 50%;
    width: max-content;
    max-width: min(440px, calc(100vw - 2 * var(--drama-space-xl)));
    transform: translateX(-50%);
  }
  .drama-toast-host--banner .drama-toast {
    min-width: 200px;
    max-width: 100%;
    padding: var(--drama-space-sm) var(--drama-space-lg);
    border: 1px solid var(--drama-color-border);
    overflow-wrap: anywhere;
  }
}
</style>
